import React, { useRef, useMemo } from 'react';
import { Canvas, extend, useFrame } from '@react-three/fiber';
import { OrbitControls, Stars, shaderMaterial } from '@react-three/drei';
import * as THREE from 'three';
import './Earth3D.css';

interface EarthSphereProps {
  textureUrl: string;
  rotationSpeed?: number;
  axialTilt?: number;
}

// Tạo shader material cho viền sáng
const RimMaterial = shaderMaterial(
  {
    color: new THREE.Color('#00ffff'),
    powerFactor: 2.0,
  },
  // Vertex shader
  `
    varying vec3 vNormal;
    varying vec3 vViewPosition;
    
    void main() {
      vNormal = normalize(normalMatrix * normal);
      vec4 mvPosition = modelViewMatrix * vec4(position, 1.0);
      vViewPosition = -mvPosition.xyz;
      gl_Position = projectionMatrix * mvPosition;
    }
  `,
  // Fragment shader
  `
    uniform vec3 color;
    uniform float powerFactor;
    
    varying vec3 vNormal;
    varying vec3 vViewPosition;
    
    void main() {
      vec3 normal = normalize(vNormal);
      vec3 viewDir = normalize(vViewPosition);
      float rim = 1.0 - max(dot(viewDir, normal), 0.0);
      rim = pow(rim, powerFactor);
      gl_FragColor = vec4(color, rim);
    }
  `
);

const EarthSphere: React.FC<EarthSphereProps> = ({
  textureUrl,
  rotationSpeed = 0.005,
  axialTilt = 23.5,
}) => {
  const groupRef = useRef<THREE.Group>(null);
  const meshRef = useRef<THREE.Mesh>(null);

  // Sử dụng useMemo để tạo texture thay vì useTexture
  const texture = useMemo(() => {
    const loader = new THREE.TextureLoader();
    const tex = loader.load(textureUrl);
    tex.wrapS = THREE.RepeatWrapping;
    tex.wrapT = THREE.RepeatWrapping;
    tex.repeat.set(1, 1);
    tex.minFilter = THREE.LinearMipmapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    tex.anisotropy = 16;
    return tex;
  }, [textureUrl]);

  useFrame((state) => {
    if (groupRef.current) {
      groupRef.current.rotation.y += rotationSpeed;
    }
    if (meshRef.current) {
      const time = state.clock.getElapsedTime();
      meshRef.current.scale.x = 1 + Math.sin(time) * 0.005;
      meshRef.current.scale.y = 1 + Math.sin(time) * 0.005;
      meshRef.current.scale.z = 1 + Math.sin(time) * 0.005;
    }
  });

  const tiltInRadians = (axialTilt * Math.PI) / 180;

  return (
    <group ref={groupRef} rotation={[tiltInRadians, 0, 0]}>
      <mesh ref={meshRef}>
        <sphereGeometry args={[2, 64, 64]} attach="geometry" />
        <meshPhysicalMaterial
          map={texture}
          metalness={0.0}
          roughness={1.0}
          clearcoat={0.1}
          clearcoatRoughness={0.4}
          side={THREE.FrontSide}
        />
      </mesh>
    </group>
  );
};

// Đăng ký custom material
extend({ RimMaterial });

interface Earth3DProps {
  textureUrl: string;
  size?: number;
  rotationSpeed?: number;
  enableControls?: boolean;
  showStars?: boolean;
}

const Earth3D: React.FC<Earth3DProps> = ({
  textureUrl,
  size = 300,
  rotationSpeed = 0.005,
  enableControls = true,
  showStars = true,
}) => {
  return (
    <div
      className="earth3d-container"
      style={{ width: `${size}px`, height: `${size}px` }}
    >
      <Canvas
        camera={{ position: [0, 0, 5], fov: 45 }}
        gl={{ antialias: true }}
      >
        <ambientLight intensity={0.8} />
        <pointLight position={[10, 10, 10]} intensity={1.5} />
        <pointLight position={[-10, -10, -10]} intensity={0.5} />
        <hemisphereLight intensity={0.3} groundColor="#000000" />

        {showStars && <Stars radius={100} depth={50} count={5000} factor={4} />}

        <EarthSphere textureUrl={textureUrl} rotationSpeed={rotationSpeed} />

        {enableControls && (
          <OrbitControls enableZoom={true} enablePan={false} />
        )}
      </Canvas>
    </div>
  );
};

export default Earth3D;
